import { useState, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import type { Employee, PertEstimate, SkillLevel } from "../../types";
import { pertExpectedDays } from "../../types";
import { useTeams } from "../../stores";
import { parseSkillsFromEmojis, evaluateRequirement, skillsToEmojiString } from "../../lib/skillEngine";
import { X, ChevronDown, ChevronRight, Check, UserCheck } from "lucide-react";

export type TimeUnit = "days" | "weeks" | "months";

export const UNIT_TO_DAYS: Record<TimeUnit, number> = {
  days: 1, weeks: 5, months: 20,
};

const formatEstimate = (estimate: PertEstimate, unit: TimeUnit, usePert: boolean): string => {
  const days = usePert ? pertExpectedDays(estimate) : estimate.m;
  const value = days / UNIT_TO_DAYS[unit];
  const rounded = Math.round(value * 10) / 10;
  const unitShort = unit === "days" ? "T" : unit === "weeks" ? "W" : "M";
  return `${rounded}${unitShort}`;
};

interface EmployeeEstimateListProps {
  employees: Employee[];
  estimates: Record<string, PertEstimate>;
  assigneeIds: string[];
  requiredSkills: Record<string, SkillLevel>;
  onAdd: (employeeId: string) => void;
  onUpdate: (employeeId: string, estimate: PertEstimate) => void;
  onRemove: (employeeId: string) => void;
  onToggleAssignee: (employeeId: string) => void;
}

type UnitState = Record<string, { o: TimeUnit; m: TimeUnit; p: TimeUnit }>;

const DEFAULT_UNIT: TimeUnit = 'weeks';

export function EmployeeEstimateList({
  employees, estimates, assigneeIds, requiredSkills,
  onAdd, onUpdate, onRemove, onToggleAssignee,
}: EmployeeEstimateListProps) {
  const { t } = useTranslation();
  const teams = useTeams();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<string | null>(null);
  const [units, setUnits] = useState<UnitState>({});
  const [pertPerEmployee, setPertPerEmployee] = useState<Record<string, boolean>>({});
  const [defaultPert, setDefaultPert] = useState(false);

  const getEmployeePert = (employeeId: string): boolean => pertPerEmployee[employeeId] ?? defaultPert;

  const handleTogglePert = (employeeId: string) => {
    const newVal = !getEmployeePert(employeeId);
    setPertPerEmployee(prev => ({ ...prev, [employeeId]: newVal }));
    setDefaultPert(newVal);
    if (!newVal) {
      const est = estimates[employeeId];
      if (est) onUpdate(employeeId, { o: est.m, m: est.m, p: est.m });
    }
  };

  const getUnits = (employeeId: string) => units[employeeId] || { o: DEFAULT_UNIT, m: DEFAULT_UNIT, p: DEFAULT_UNIT };

  const hasSkillMatch = (employee: Employee): boolean => {
    const requiredEmoji = skillsToEmojiString(requiredSkills);
    if (!requiredEmoji) return true;
    const employeeSkills = parseSkillsFromEmojis(employee.skills);
    return evaluateRequirement(requiredEmoji, employeeSkills).matches;
  };

  const sortedEmployees = useMemo(() => {
    let filtered = employees;
    if (selectedTeam) filtered = employees.filter((e) => e.team === selectedTeam);
    return [...filtered].sort((a, b) => {
      const aMatch = hasSkillMatch(a);
      const bMatch = hasSkillMatch(b);
      if (aMatch && !bMatch) return -1;
      if (!aMatch && bMatch) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [employees, selectedTeam, requiredSkills]);

  const hasRequirements = Object.values(requiredSkills).some((level) => level > 0);

  const handleAdd = (employeeId: string) => {
    const previousUnits = expandedId ? getUnits(expandedId) : null;
    setUnits((prev) => ({ ...prev, [employeeId]: previousUnits || { o: DEFAULT_UNIT, m: DEFAULT_UNIT, p: DEFAULT_UNIT } }));
    onAdd(employeeId);
    if (expandedId && estimates[expandedId]) onUpdate(employeeId, { ...estimates[expandedId] });
    setExpandedId(employeeId);
  };

  const handleRowClick = (employeeId: string) => {
    if (employeeId in estimates) setExpandedId(expandedId === employeeId ? null : employeeId);
    else handleAdd(employeeId);
  };

  const handleUnitChange = (employeeId: string, field: "o" | "m" | "p", unit: TimeUnit) => {
    setUnits((prev) => ({ ...prev, [employeeId]: { ...getUnits(employeeId), [field]: unit } }));
  };

  const toDisplay = (days: number, unit: TimeUnit) => Math.round((days / UNIT_TO_DAYS[unit]) * 100) / 100;
  const toDays = (value: number, unit: TimeUnit) => Math.round(value * UNIT_TO_DAYS[unit]);

  const handleEstimateChange = (employeeId: string, field: keyof PertEstimate, value: number, unit: TimeUnit) => {
    const estimate = estimates[employeeId];
    if (!estimate) return;
    const days = toDays(Math.max(0.01, value), unit);
    if (getEmployeePert(employeeId)) onUpdate(employeeId, { ...estimate, [field]: days });
    else onUpdate(employeeId, { o: days, m: days, p: days });
  };

  const unitLabelMap: Record<TimeUnit, string> = {
    days: t('estimates.days'),
    weeks: t('estimates.weeks'),
    months: t('estimates.months'),
  };

  const UnitSelect = ({ value, onChange }: { value: TimeUnit; onChange: (u: TimeUnit) => void }) => (
    <select value={value} onChange={(e) => onChange(e.target.value as TimeUnit)}
      className="px-2 py-0.5 text-[10px] rounded border border-gray-200 bg-gray-50 text-gray-700">
      {Object.entries(unitLabelMap).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
    </select>
  );

  return (
    <div className="space-y-1.5">
      {/* Team Filter */}
      <div className="flex gap-1 flex-wrap">
        <button type="button" onClick={() => setSelectedTeam(null)}
          className={`px-2 py-0.5 text-[10px] rounded-full transition-colors ${selectedTeam === null ? "bg-gray-800 text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}>
          {t('common.all')}
        </button>
        {teams.map((team) => (
          <button key={team.id} type="button" onClick={() => setSelectedTeam(team.id)}
            className={`px-2 py-0.5 text-[10px] rounded-full transition-colors ${selectedTeam === team.id ? "bg-gray-800 text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}>
            {team.name}
          </button>
        ))}
      </div>

      {/* Employee List */}
      <div className="border border-gray-200 rounded divide-y divide-gray-100 max-h-60 overflow-y-auto">
        {sortedEmployees.map((employee) => {
          const hasEstimate = employee.id in estimates;
          const estimate = estimates[employee.id];
          const isExpanded = expandedId === employee.id;
          const isAssigned = assigneeIds.includes(employee.id);
          const matches = hasSkillMatch(employee);
          const showMatchIndicator = hasRequirements && !hasEstimate;
          const employeeUnits = getUnits(employee.id);

          return (
            <div key={employee.id} className="bg-white">
              <div
                className={`flex items-center gap-2 px-2 py-1 transition-colors cursor-pointer ${
                  hasEstimate ? "bg-blue-50 hover:bg-blue-100"
                    : matches && hasRequirements ? "hover:bg-green-50 bg-green-50/30"
                    : "hover:bg-gray-50"
                }`}
                onClick={() => handleRowClick(employee.id)}
              >
                <div className="w-3 shrink-0">
                  {hasEstimate
                    ? (isExpanded ? <ChevronDown className="w-3 h-3 text-gray-400" /> : <ChevronRight className="w-3 h-3 text-gray-400" />)
                    : <ChevronRight className="w-3 h-3 text-gray-300" />}
                </div>
                <div className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[9px] shrink-0"
                  style={{ backgroundColor: employee.color }}>
                  {employee.name.charAt(0)}
                </div>
                <span className="text-[10px] flex-1 text-gray-700">{employee.name}</span>
                <span className={`text-[10px] ${showMatchIndicator && matches ? "opacity-100" : "opacity-60"}`}>
                  {employee.skills || "—"}
                </span>
                {isAssigned && (
                  <span className="px-1 py-px text-[9px] bg-green-100 text-green-700 rounded flex items-center gap-0.5">
                    <UserCheck className="w-2.5 h-2.5" /> {t('employees.assigned')}
                  </span>
                )}
                {hasEstimate ? (
                  <span className="px-1 py-px text-[9px] bg-blue-100 text-blue-700 rounded">
                    {formatEstimate(estimate, employeeUnits.m, getEmployeePert(employee.id))}
                  </span>
                ) : showMatchIndicator ? (
                  <span className={`w-4 h-4 rounded-full flex items-center justify-center ${matches ? "bg-green-500 text-white" : "bg-gray-200 text-gray-400"}`}>
                    {matches ? <Check className="w-2.5 h-2.5" /> : "—"}
                  </span>
                ) : null}
                {hasEstimate && (
                  <button type="button" onClick={(e) => { e.stopPropagation(); onRemove(employee.id); if (expandedId === employee.id) setExpandedId(null); }}
                    className="p-0.5 text-gray-400 hover:text-red-500 transition-colors">
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>

              {hasEstimate && isExpanded && (
                <div className="px-2 py-1.5 bg-blue-50/50 border-t border-blue-100">
                  <div className="pl-8 space-y-1.5">
                    <div className="flex items-center gap-1.5">
                      <button type="button" onClick={() => handleTogglePert(employee.id)}
                        className={`px-2 py-0.5 text-[10px] rounded border transition-colors ${getEmployeePert(employee.id) ? 'bg-blue-600 text-white border-blue-600' : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'}`}
                      >PERT</button>
                      {!getEmployeePert(employee.id) && (
                        <>
                          <input type="number" min="0.01" step="any"
                            value={toDisplay(estimate.m, employeeUnits.m)}
                            onChange={(e) => handleEstimateChange(employee.id, "m", Number(e.target.value), employeeUnits.m)}
                            className="w-14 px-1.5 py-0.5 text-[10px] border border-gray-200 rounded bg-white text-gray-900 focus:ring-1 focus:ring-blue-500" />
                          <UnitSelect value={employeeUnits.m} onChange={(u) => handleUnitChange(employee.id, "m", u)} />
                        </>
                      )}
                    </div>
                    {getEmployeePert(employee.id) && (
                      <div className="grid grid-cols-3 gap-1.5">
                        {(['o', 'm', 'p'] as const).map((field) => {
                          const label = field === 'o' ? t('estimates.optimistic') : field === 'm' ? t('estimates.realistic') : t('estimates.pessimistic');
                          const color = field === 'o' ? 'text-green-600' : field === 'm' ? 'text-blue-600' : 'text-orange-600';
                          return (
                            <div key={field}>
                              <span className={`text-[9px] ${color}`}>{label}</span>
                              <div className="flex gap-0.5">
                                <input type="number" min="0.01" step="any"
                                  value={toDisplay(estimate[field], employeeUnits[field])}
                                  onChange={(e) => handleEstimateChange(employee.id, field, Number(e.target.value), employeeUnits[field])}
                                  className="w-12 px-1 py-0.5 text-[10px] border border-gray-200 rounded bg-white text-gray-900 focus:ring-1 focus:ring-blue-500" />
                                <UnitSelect value={employeeUnits[field]} onChange={(u) => handleUnitChange(employee.id, field, u)} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <button type="button" onClick={() => onToggleAssignee(employee.id)}
                      className={`flex items-center gap-1 px-2 py-0.5 text-[10px] rounded transition-colors ${
                        isAssigned ? "bg-green-100 text-green-700 hover:bg-green-200" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}>
                      <UserCheck className="w-3 h-3" />
                      {isAssigned ? t('employees.assigned') : t('employees.assign')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {sortedEmployees.length === 0 && (
          <div className="px-2 py-2 text-[10px] text-gray-500 text-center">{t('employees.noEmployeesInSelectedTeam')}</div>
        )}
      </div>

      {Object.keys(estimates).length === 0 && (
        <p className="text-[10px] text-gray-400 text-center">{t('employees.clickToAddEstimate')}</p>
      )}
    </div>
  );
}
